<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h4 class="text-lg font-semibold font-heading text-gray-900 dark:text-gray-100">Final Audit & Scoring</h4>
      <div class="flex items-center gap-3">
        <span v-if="audit" class="px-3 py-1 rounded-full text-sm font-medium" :class="auditStatusClass">
          {{ auditStatusLabel }}
        </span>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4">
      <p class="text-sm text-red-700 dark:text-red-400">{{ error }}</p>
    </div>

    <!-- Progress -->
    <div v-if="progressMessage" class="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-xl">
      <div class="flex items-center gap-3">
        <svg class="animate-spin h-5 w-5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span class="text-sm text-primary-700 dark:text-primary-300">{{ progressMessage }}</span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <svg class="animate-spin h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>

    <!-- No audit yet — show start button -->
    <div v-else-if="!audit && canStart" class="text-center py-8">
      <div class="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h4 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100 mb-2">Run Final Audit</h4>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
        Generate a comprehensive audit with before/after scoring across 14 optimization dimensions.
      </p>
      <button @click="handleRunAudit" :disabled="starting"
        class="inline-flex items-center px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-[#12e40b] hover:bg-[#0fd609] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        {{ starting ? 'Starting...' : 'Run Final Audit' }}
      </button>
    </div>

    <!-- Audit Results -->
    <template v-else-if="audit && (audit.status === 'ready' || audit.accepted)">
      <!-- Score Summary Card -->
      <div class="bg-gradient-to-r from-primary-50 to-primary-50 dark:from-primary-900/20 dark:to-primary-900/20 rounded-xl p-6">
        <div class="flex items-center justify-between mb-4">
          <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100">Optimization Score</h5>
          <span v-if="audit.score_improvement !== null" class="text-lg font-bold"
            :class="audit.score_improvement >= 0 ? 'text-green-600' : 'text-red-600'">
            {{ audit.score_improvement >= 0 ? '+' : '' }}{{ audit.score_improvement }}
          </span>
        </div>
        <div class="grid grid-cols-2 gap-6">
          <div>
            <div class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Before</div>
            <div class="text-3xl font-bold text-gray-400">{{ formatScore(audit.total_score_before) }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">After</div>
            <div class="text-3xl font-bold text-primary-600 dark:text-primary-400">{{ formatScore(audit.total_score_after) }}</div>
          </div>
        </div>
        <!-- Score bar -->
        <div class="mt-4 h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div class="h-full bg-primary-500 rounded-full transition-all duration-500"
            :style="{ width: `${audit.total_score_after || 0}%` }" />
        </div>
      </div>

      <!-- Sub-tabs -->
      <div class="border-b border-gray-200 dark:border-gray-700">
        <nav class="flex gap-4 -mb-px overflow-x-auto">
          <button v-for="st in subTabs" :key="st.key" @click="activeSubTab = st.key"
            :class="['pb-2 px-1 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
              activeSubTab === st.key
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 cursor-pointer']">
            {{ st.label }}
          </button>
        </nav>
      </div>

      <!-- Dimension Scores -->
      <div v-if="activeSubTab === 'scores'" class="space-y-3">
        <div v-for="(data, dim) in audit.score_after" :key="dim"
          class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
          <div class="flex items-center justify-between mb-1">
            <span class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ formatDimension(dim) }}</span>
            <div class="flex items-center gap-3 text-xs">
              <span class="text-gray-400">{{ (data.weight * 100).toFixed(0) }}%</span>
              <span class="text-gray-500">{{ beforeScore(dim) }}</span>
              <span class="font-medium" :class="data.score >= beforeScore(dim) ? 'text-green-600' : 'text-red-600'">
                {{ data.score }}
              </span>
            </div>
          </div>
          <div class="flex gap-1 h-2">
            <div class="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div class="h-full bg-gray-400 rounded-full" :style="{ width: `${beforeScore(dim)}%` }" />
            </div>
            <div class="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div class="h-full rounded-full" :class="data.score >= 70 ? 'bg-green-500' : data.score >= 40 ? 'bg-amber-500' : 'bg-red-500'"
                :style="{ width: `${data.score}%` }" />
            </div>
          </div>
        </div>
      </div>

      <!-- Density Tab -->
      <div v-if="activeSubTab === 'density'" class="space-y-4">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th class="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Keyword</th>
                <th class="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">Body</th>
                <th class="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">FAQ</th>
                <th class="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">Total</th>
                <th class="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">Target</th>
                <th class="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">Delta</th>
                <th class="text-center py-2 px-2 text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(data, keyword) in audit.total_density" :key="keyword"
                class="border-b border-gray-100 dark:border-gray-800">
                <td class="py-2 px-2 font-medium text-gray-900 dark:text-gray-100">{{ keyword }}</td>
                <td class="py-2 px-2 text-right">{{ data.body_count }}</td>
                <td class="py-2 px-2 text-right">{{ data.faq_count }}</td>
                <td class="py-2 px-2 text-right font-medium">{{ data.total_count }}</td>
                <td class="py-2 px-2 text-right">{{ data.target }}</td>
                <td class="py-2 px-2 text-right" :class="deltaClass(data.delta)">{{ data.delta > 0 ? '+' : '' }}{{ data.delta }}</td>
                <td class="py-2 px-2 text-center">
                  <span class="px-2 py-0.5 rounded-full text-xs font-medium" :class="densityStatusClass(data.status)">
                    {{ data.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Headings Tab -->
      <div v-if="activeSubTab === 'headings'" class="space-y-4">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th class="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Keyword</th>
                <th class="text-center py-2 px-2 text-xs font-medium text-gray-500 uppercase">H1 Target</th>
                <th class="text-center py-2 px-2 text-xs font-medium text-gray-500 uppercase">H1 Actual</th>
                <th class="text-center py-2 px-2 text-xs font-medium text-gray-500 uppercase">H2 Target</th>
                <th class="text-center py-2 px-2 text-xs font-medium text-gray-500 uppercase">H2 Actual</th>
                <th class="text-center py-2 px-2 text-xs font-medium text-gray-500 uppercase">H3 Target</th>
                <th class="text-center py-2 px-2 text-xs font-medium text-gray-500 uppercase">H3 Actual</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(data, keyword) in audit.heading_density" :key="keyword"
                class="border-b border-gray-100 dark:border-gray-800">
                <td class="py-2 px-2 font-medium text-gray-900 dark:text-gray-100">{{ keyword }}</td>
                <td class="py-2 px-2 text-center">{{ data.h1_target }}</td>
                <td class="py-2 px-2 text-center" :class="headingMatch(data.h1_actual, data.h1_target)">{{ data.h1_actual }}</td>
                <td class="py-2 px-2 text-center">{{ data.h2_target }}</td>
                <td class="py-2 px-2 text-center" :class="headingMatch(data.h2_actual, data.h2_target)">{{ data.h2_actual }}</td>
                <td class="py-2 px-2 text-center">{{ data.h3_target }}</td>
                <td class="py-2 px-2 text-center" :class="headingMatch(data.h3_actual, data.h3_target)">{{ data.h3_actual }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Placement Tab -->
      <div v-if="activeSubTab === 'placement'" class="space-y-4">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th class="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Keyword</th>
                <th class="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Position</th>
                <th class="text-center py-2 px-2 text-xs font-medium text-gray-500 uppercase">Required</th>
                <th class="text-center py-2 px-2 text-xs font-medium text-gray-500 uppercase">Present</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entry, idx) in audit.placement_table" :key="idx"
                class="border-b border-gray-100 dark:border-gray-800">
                <td class="py-2 px-2 font-medium text-gray-900 dark:text-gray-100">{{ entry.keyword }}</td>
                <td class="py-2 px-2 text-gray-600 dark:text-gray-400">{{ formatPosition(entry.position) }}</td>
                <td class="py-2 px-2 text-center">
                  <span v-if="entry.required" class="text-amber-500">Required</span>
                  <span v-else class="text-gray-400">Optional</span>
                </td>
                <td class="py-2 px-2 text-center">
                  <span v-if="entry.present" class="text-green-600">Present</span>
                  <span v-else class="text-red-500">Missing</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Word Count Tab -->
      <div v-if="activeSubTab === 'words'" class="space-y-4">
        <div v-if="audit.word_counts" class="grid grid-cols-2 gap-4">
          <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <div class="text-xs text-gray-500 uppercase mb-1">Body Words</div>
            <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ audit.word_counts.body_words }}</div>
          </div>
          <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <div class="text-xs text-gray-500 uppercase mb-1">FAQ Words</div>
            <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ audit.word_counts.faq_words }}</div>
          </div>
          <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <div class="text-xs text-gray-500 uppercase mb-1">Total</div>
            <div class="text-2xl font-bold text-primary-600">{{ audit.word_counts.total_words }}</div>
          </div>
          <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <div class="text-xs text-gray-500 uppercase mb-1">Target</div>
            <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ audit.word_counts.target_words }}</div>
          </div>
        </div>
        <div v-if="audit.word_counts" class="flex items-center gap-3 text-sm">
          <span class="text-gray-500">Delta:</span>
          <span :class="deltaClass(audit.word_counts.delta)" class="font-medium">
            {{ audit.word_counts.delta > 0 ? '+' : '' }}{{ audit.word_counts.delta }} ({{ audit.word_counts.delta_pct }}%)
          </span>
          <span class="px-2 py-0.5 rounded-full text-xs font-medium" :class="densityStatusClass(audit.word_counts.status)">
            {{ audit.word_counts.status }}
          </span>
        </div>
      </div>

      <!-- Readability Tab -->
      <div v-if="activeSubTab === 'readability'" class="space-y-4">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th class="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Metric</th>
                <th class="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">Competitor Avg</th>
                <th class="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">Before</th>
                <th class="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">After</th>
                <th class="text-right py-2 px-2 text-xs font-medium text-gray-500 uppercase">Improvement</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(data, metric) in audit.readability_comparison" :key="metric"
                class="border-b border-gray-100 dark:border-gray-800">
                <td class="py-2 px-2 font-medium text-gray-900 dark:text-gray-100">{{ formatMetric(metric) }}</td>
                <td class="py-2 px-2 text-right text-gray-500">{{ data.competitor_avg ?? '-' }}</td>
                <td class="py-2 px-2 text-right text-gray-500">{{ data.client_before ?? '-' }}</td>
                <td class="py-2 px-2 text-right font-medium text-gray-900 dark:text-gray-100">{{ data.client_after ?? '-' }}</td>
                <td class="py-2 px-2 text-right" :class="data.improvement > 0 ? 'text-green-600' : data.improvement < 0 ? 'text-red-500' : 'text-gray-500'">
                  {{ data.improvement !== null ? (data.improvement > 0 ? '+' : '') + data.improvement : '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Changelog Tab -->
      <div v-if="activeSubTab === 'changelog'" class="space-y-3">
        <div v-for="(entry, idx) in audit.changelog" :key="idx"
          class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3">
          <div class="flex items-center gap-2 mb-1">
            <span class="px-2 py-0.5 rounded-lg text-xs font-medium" :class="changelogCategoryClass(entry.category)">
              {{ entry.category }}
            </span>
            <span class="text-xs text-gray-400">{{ entry.field }}</span>
          </div>
          <p class="text-sm text-gray-700 dark:text-gray-300">{{ entry.summary }}</p>
        </div>
      </div>

      <!-- Deliverables Tab -->
      <div v-if="activeSubTab === 'deliverables'" class="space-y-4">
        <div v-if="audit.spreadsheet_file" class="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <div>
            <div class="text-sm font-medium text-gray-900 dark:text-gray-100">Audit Spreadsheet</div>
            <div class="text-xs text-gray-500">XLSX with all audit data tabs</div>
          </div>
          <button @click="handleDownload('spreadsheet')" class="btn btn-secondary btn-sm">Download</button>
        </div>
        <div v-if="audit.content_file" class="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <div>
            <div class="text-sm font-medium text-gray-900 dark:text-gray-100">Optimized Content</div>
            <div class="text-xs text-gray-500">HTML content with all sections and FAQs</div>
          </div>
          <button @click="handleDownload('content')" class="btn btn-secondary btn-sm">Download</button>
        </div>
        <div v-if="audit.schema_file" class="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <div>
            <div class="text-sm font-medium text-gray-900 dark:text-gray-100">JSON-LD Schema</div>
            <div class="text-xs text-gray-500">Structured data for this page</div>
          </div>
          <button @click="handleDownload('schema')" class="btn btn-secondary btn-sm">Download</button>
        </div>

        <!-- Accept deliverable -->
        <div v-if="!audit.accepted" class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button @click="handleAccept" :disabled="accepting"
            class="inline-flex items-center px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-[#12e40b] hover:bg-[#0fd609] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
            {{ accepting ? 'Accepting...' : 'Accept Deliverable' }}
          </button>
        </div>
        <div v-else class="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center">
          <p class="text-sm font-medium text-green-700 dark:text-green-400">Deliverable accepted</p>
        </div>
      </div>
    </template>

    <!-- Running / Pending states -->
    <div v-else-if="audit && ['pending', 'counting', 'scoring', 'packaging'].includes(audit.status)" class="text-center py-8">
      <svg class="animate-spin h-10 w-10 text-primary-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
      <p class="text-sm text-gray-500">Running final audit...</p>
    </div>

    <!-- Failed -->
    <div v-else-if="audit && audit.status === 'failed'" class="text-center py-8">
      <div class="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <p class="text-sm text-red-600 mb-2">Final audit failed</p>
      <p v-if="audit.error_message" class="text-xs text-gray-500 mb-4">{{ audit.error_message }}</p>
      <button @click="handleRunAudit" :disabled="starting"
        class="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 transition-colors">
        Retry
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { runFinalAudit, getFinalAudit, acceptFinalAudit, downloadDeliverable } from '~/javascript/api/final_audit.js'
import consumer from '~/channels/consumer'

const props = defineProps({
  pageId: { type: String, required: true },
  canStart: { type: Boolean, default: false }
})

const audit = ref(null)
const loading = ref(false)
const starting = ref(false)
const accepting = ref(false)
const error = ref(null)
const progressMessage = ref('')
const activeSubTab = ref('scores')
let subscription = null
let pollTimer = null

const subTabs = [
  { key: 'scores', label: 'Scores' },
  { key: 'density', label: 'Density' },
  { key: 'headings', label: 'Headings' },
  { key: 'placement', label: 'Placement' },
  { key: 'words', label: 'Word Count' },
  { key: 'readability', label: 'Readability' },
  { key: 'changelog', label: 'Changelog' },
  { key: 'deliverables', label: 'Deliverables' }
]

const auditStatusClass = computed(() => {
  const map = {
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    counting: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    scoring: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    packaging: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    ready: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    failed: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
  }
  return map[audit.value?.status] || 'bg-gray-100 text-gray-700'
})

const auditStatusLabel = computed(() => {
  const map = {
    pending: 'Pending',
    counting: 'Counting',
    scoring: 'Scoring',
    packaging: 'Packaging',
    ready: 'Ready',
    failed: 'Failed'
  }
  return map[audit.value?.status] || audit.value?.status || ''
})

const formatScore = (score) => score != null ? score.toFixed(1) : '-'

const formatDimension = (dim) => dim.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

const formatPosition = (pos) => pos.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

const formatMetric = (metric) => metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())

const beforeScore = (dim) => audit.value?.score_before?.[dim]?.score || 0

const deltaClass = (delta) => {
  if (delta === 0) return 'text-green-600'
  if (delta > 0) return 'text-amber-500'
  return 'text-red-500'
}

const densityStatusClass = (status) => {
  const map = {
    on_target: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    over: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    under: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
  }
  return map[status] || 'bg-gray-100 text-gray-700'
}

const headingMatch = (actual, target) => {
  if (target === 0) return 'text-gray-400'
  return actual >= target ? 'text-green-600 font-medium' : 'text-red-500 font-medium'
}

const changelogCategoryClass = (cat) => {
  const map = {
    meta: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    structure: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    content: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    faq: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    schema: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    audit: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
  }
  return map[cat] || 'bg-gray-100 text-gray-700'
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const response = await getFinalAudit(props.pageId)
      const a = response.data.final_audit
      if (a && !['pending', 'counting', 'scoring', 'packaging'].includes(a.status)) {
        audit.value = a
        progressMessage.value = ''
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const fetchAudit = async () => {
  try {
    loading.value = true
    const response = await getFinalAudit(props.pageId)
    audit.value = response.data.final_audit
  } catch (e) {
    if (e.response?.status !== 404) {
      console.error('Error fetching final audit:', e) // eslint-disable-line no-console
    }
  } finally {
    loading.value = false
  }
}

const subscribeToChannel = () => {
  if (subscription) subscription.unsubscribe()
  subscription = consumer.subscriptions.create(
    { channel: 'FinalAuditChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (['ready', 'failed'].includes(data.step)) {
          stopPolling()
          fetchAudit()
          progressMessage.value = ''
        }
      }
    }
  )
}

const handleRunAudit = async () => {
  try {
    starting.value = true
    error.value = null
    await runFinalAudit(props.pageId)
    subscribeToChannel()
    audit.value = { status: 'pending' }
    startPolling()
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to start final audit'
    console.error('Failed to start final audit:', e) // eslint-disable-line no-console
  } finally {
    starting.value = false
  }
}

const handleAccept = async () => {
  try {
    accepting.value = true
    error.value = null
    const response = await acceptFinalAudit(props.pageId)
    audit.value = response.data.final_audit
  } catch (e) {
    error.value = e.response?.data?.message || 'Failed to accept deliverable'
    console.error('Failed to accept:', e) // eslint-disable-line no-console
  } finally {
    accepting.value = false
  }
}

const handleDownload = async (type) => {
  try {
    const response = await downloadDeliverable(props.pageId, type)
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    const ext = type === 'spreadsheet' ? 'xlsx' : type === 'schema' ? 'json' : 'html'
    link.setAttribute('download', `${type}.${ext}`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (e) {
    error.value = `Download failed: ${e.message}`
    console.error('Download failed:', e) // eslint-disable-line no-console
  }
}

onMounted(() => {
  fetchAudit()
  subscribeToChannel()
})

onUnmounted(() => {
  if (subscription) {
    subscription.unsubscribe()
    subscription = null
  }
  stopPolling()
})
</script>
